<template>
  <a-modal
    v-model:visible="isVisible"
    :title="null"
    :footer="null"
    width="90%"
    :body-style="{ padding: 0 }"
    centered
    destroy-on-close
    class="pdf-preview-modal"
    :closable="false"
  >
    <div class="modal-header">
      <h3 class="modal-title">{{ title }}</h3>
      <div class="header-actions">
        <button type="primary" class="download-button" @click="handleDownload">
          <DownloadOutlined />
          <span class="btn-text">DOWNLOAD</span>
        </button>
        <a-button type="text" class="close-button" @click="handleClose">
          <CloseOutlined />
        </a-button>
      </div>
    </div>

    <div class="modal-content">
      <template v-if="pdfList.length > 1">
        <button class="nav-button nav-prev" @click="prevPdf" :disabled="currentIndex === 0">
          <LeftOutlined />
        </button>
        <button
          class="nav-button nav-next"
          @click="nextPdf"
          :disabled="currentIndex === pdfList.length - 1"
        >
          <RightOutlined />
        </button>
      </template>

      <div class="pdf-container">
        <div v-if="loading" class="pdf-loading">
          <a-spin size="large" />
          <p>{{ isImageFile ? 'Memuat gambar...' : 'Memuat PDF...' }}</p>
        </div>

        <div v-if="error" class="pdf-error">
          <FileOutlined class="error-icon" />
          <p>{{ error }}</p>
        </div>

        <!-- Image Viewer (for PNG, JPG, etc.) -->
        <div v-if="!loading && !error && currentPdfUrl && isImageFile" class="image-viewer-wrapper">
          <div class="image-controls">
            <div class="zoom-controls">
              <a-button size="small" @click="changeImageScale(-0.25)" :disabled="imageScale <= 0.5">
                <MinusOutlined />
              </a-button>
              <span class="zoom-level">{{ Math.round(imageScale * 100) }}%</span>
              <a-button size="small" @click="changeImageScale(0.25)" :disabled="imageScale >= 3">
                <PlusOutlined />
              </a-button>
            </div>
          </div>

          <div class="image-canvas-wrapper">
            <img
              :src="currentPdfUrl"
              :alt="title"
              :style="{ transform: `scale(${imageScale})` }"
              class="preview-image"
            />
          </div>
        </div>

        <!-- PDF Viewer (for PDF files) -->
        <div v-show="!loading && !error && currentPdfUrl && !isImageFile" class="pdf-viewer-wrapper">
          <div class="pdf-controls">
            <a-button size="small" @click="previousPage" :disabled="currentPage <= 1">
              <LeftOutlined />
            </a-button>
            <span class="page-info">
              Hal {{ currentPage }} / {{ numPages }}
            </span>
            <a-button size="small" @click="nextPage" :disabled="currentPage >= numPages">
              <RightOutlined />
            </a-button>

            <div class="zoom-controls">
              <a-button size="small" @click="changeScale(-0.25)" :disabled="scale <= 0.5">
                <MinusOutlined />
              </a-button>
              <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
              <a-button size="small" @click="changeScale(0.25)" :disabled="scale >= 3">
                <PlusOutlined />
              </a-button>
            </div>
          </div>

          <div class="pdf-canvas-wrapper">
            <canvas ref="pdfCanvas" :key="canvasKey"></canvas>
          </div>
        </div>

        <div v-if="!currentPdfUrl && !loading" class="no-pdf">
          <FileOutlined class="no-pdf-icon" />
          <p>Tidak ada dokumen yang tersedia</p>
        </div>
      </div>
    </div>

    <div v-if="pdfList.length > 1" class="pdf-info-bar">
      <span class="pdf-name">{{ currentPdfName }}</span>
      <span class="pdf-counter">{{ currentIndex + 1 }} / {{ pdfList.length }}</span>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import {
  DownloadOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  FileOutlined,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons-vue'
import { getFileUrl } from '@/services/axios/perki'

export default defineComponent({
  name: 'PDFPreviewModal',
  components: {
    DownloadOutlined,
    CloseOutlined,
    LeftOutlined,
    RightOutlined,
    FileOutlined,
    PlusOutlined,
    MinusOutlined,
  },
  props: {
    visible: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    title: { type: String, default: 'Preview Surat Keluar' },
    pdfPath: { type: String, default: '' },
    pdfList: { type: Array, default: () => [] },
    initialIndex: { type: Number, default: 0 },
  },
  emits: ['update:visible', 'update:open', 'close'],
  setup(props, { emit }) {
    const canvasKey = ref(0)
    const currentIndex = ref(props.initialIndex)

    // Watch for initialIndex changes to sync currentIndex
    watch(() => props.initialIndex, (newIndex) => {
      currentIndex.value = newIndex
    })

    // PDF State
    const pdfCanvas = ref(null)
    const currentPage = ref(1)
    const numPages = ref(0)
    const scale = ref(1.0) // Default 100%
    const loading = ref(false)
    const error = ref('')
    let currentLoadUrl = null // Track current loading URL to detect cancellation

    // Image State
    const imageScale = ref(1.0) // Default 100% for images

    // PDF.js references
    let pdfDoc = null
    let pdfjsLib = null
    let renderTask = null // Track current render task for cancellation

    // Sync visibility
    const isVisible = computed({
      get: () => props.open || props.visible,
      set: val => {
        emit('update:open', val)
        emit('update:visible', val)
      },
    })

    // Compute URL
    const currentPdfUrl = computed(() => {
      let path = ''
      if (props.pdfPath) {
        path = props.pdfPath
      } else if (props.pdfList.length > 0 && props.pdfList[currentIndex.value]) {
        path = props.pdfList[currentIndex.value].path || props.pdfList[currentIndex.value].pdfPath
      }

      if (!path) return ''

      // Blob or HTTP check
      if (path.startsWith('blob:') || path.startsWith('http')) return path

      return getFileUrl(path)
    })

    const currentPdfName = computed(() => {
      if (props.pdfList.length > 0 && props.pdfList[currentIndex.value]) {
        return props.pdfList[currentIndex.value].name || `Document ${currentIndex.value + 1}`
      }
      return ''
    })

    // Check if current file is an image
    const isImageFile = computed(() => {
      const url = currentPdfUrl.value
      if (!url) return false

      // Extract filename from URL
      let filename = ''
      if (props.pdfPath) {
        filename = props.pdfPath
      } else if (props.pdfList.length > 0 && props.pdfList[currentIndex.value]) {
        filename = props.pdfList[currentIndex.value].path || props.pdfList[currentIndex.value].pdfPath || props.pdfList[currentIndex.value].name || ''
      }

      if (!filename) {
        // Try to extract from URL
        try {
          const urlObj = new URL(url)
          filename = urlObj.pathname
        } catch {
          filename = url
        }
      }

      const ext = filename.toLowerCase().split('.').pop()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
    })

    // Load PDF.js from CDN
    const loadPDFJS = () => {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
          resolve(window.pdfjsLib)
          return
        }
        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
        script.onload = () => {
          if (!window.pdfjsLib) {
             reject(new Error('PDF.js library not found after load'))
             return
          }
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
          resolve(window.pdfjsLib)
        }
        script.onerror = (e) => reject(new Error('Failed to load PDF.js script: ' + e))
        document.head.appendChild(script)
      })
    }

    // Cleanup function to cancel renders and destroy old PDF
    const cleanupPDF = async () => {
      // Cancel any ongoing render task
      if (renderTask) {
        try {
          renderTask.cancel()
        } catch (e) {
          // Ignore cancellation errors
        }
        renderTask = null
      }

      // Destroy old PDF document
      if (pdfDoc) {
        try {
          await pdfDoc.destroy()
        } catch (e) {
          // Ignore destroy errors
        }
        pdfDoc = null
      }

      // Clear canvas
      if (pdfCanvas.value) {
        const context = pdfCanvas.value.getContext('2d')
        if (context) {
          context.clearRect(0, 0, pdfCanvas.value.width, pdfCanvas.value.height)
        }
      }
    }

    const loadPDF = async (url) => {
      if (!url) return

      // If it's an image file, skip PDF.js loading
      if (isImageFile.value) {
        loading.value = false
        error.value = ''
        return
      }

      // Set this URL as the current load
      const loadId = url
      currentLoadUrl = loadId

      try {
        loading.value = true
        error.value = ''

        // Clean up any previous PDF and renders
        await cleanupPDF()

        // Check if we were cancelled during cleanup
        if (currentLoadUrl !== loadId) {
          console.log('loadPDF: Cancelled during cleanup')
          return
        }

        // Increment canvas key to force new canvas element
        canvasKey.value++
        await nextTick()

        // Check if we were cancelled during nextTick
        if (currentLoadUrl !== loadId) {
          console.log('loadPDF: Cancelled during canvas refresh')
          return
        }

        if (!pdfjsLib) {
          pdfjsLib = await loadPDFJS()
        }

        const loadingTask = pdfjsLib.getDocument(url)
        pdfDoc = await loadingTask.promise

        // Check if we were cancelled during PDF load
        if (currentLoadUrl !== loadId) {
          console.log('loadPDF: Cancelled during PDF load')
          if (pdfDoc) {
            await pdfDoc.destroy()
            pdfDoc = null
          }
          return
        }

        numPages.value = pdfDoc.numPages
        currentPage.value = 1

        // Render first page after DOM update
        await nextTick()

        // Final check before rendering
        if (currentLoadUrl !== loadId) {
          console.log('loadPDF: Cancelled before render')
          return
        }

        await renderPage(1)
      } catch (err) {
        // Only show error if this load wasn't cancelled
        if (currentLoadUrl === loadId) {
          console.error('Error loading PDF:', err)
          error.value = 'Gagal memuat PDF. Silakan coba lagi atau unduh file.'
        }
      } finally {
        // Only clear loading if this load wasn't cancelled
        if (currentLoadUrl === loadId) {
          loading.value = false
          currentLoadUrl = null
        }
      }
    }

   // Render a specific page to canvas
    const renderPage = async (pageNum) => {
      if (!pdfDoc) {
        console.log('renderPage: No pdfDoc available')
        return
      }

      if (!pdfCanvas.value) {
        console.log('renderPage: Canvas ref not available')
        return
      }

      try {
        // Cancel any ongoing render task
        if (renderTask) {
          try {
            renderTask.cancel()
          } catch(e) { /* ignore cancel error */ }
          renderTask = null
        }

        const page = await pdfDoc.getPage(pageNum)
        const viewport = page.getViewport({ scale: scale.value })

        const canvas = pdfCanvas.value
        const context = canvas.getContext('2d')

        // Tidak perlu clearRect karena canvas ini baru dibuat

        // High DPI Support
        const outputScale = window.devicePixelRatio || 1

        canvas.width = Math.floor(viewport.width * outputScale)
        canvas.height = Math.floor(viewport.height * outputScale)

        canvas.style.width = Math.floor(viewport.width) + 'px'
        canvas.style.height = Math.floor(viewport.height) + 'px'

        const transform = outputScale !== 1
          ? [outputScale, 0, 0, outputScale, 0, 0]
          : null

        const renderContext = {
          canvasContext: context,
          transform: transform,
          viewport: viewport,
        }

        renderTask = page.render(renderContext)
        await renderTask.promise
        renderTask = null
      } catch (err) {
        if (err.name === 'RenderingCancelledException') {
          // Render dicancel (wajar jika user klik next/prev cepat-cepat)
          return
        }
        console.error('Error rendering page:', err)
        error.value = 'Gagal menampilkan halaman.'
      }
    }

    // Consolidated Watcher
    watch(
      [isVisible, currentPdfUrl],
      async ([newVisible, newUrl], [oldVisible, oldUrl]) => {
        // If modal is visible
        if (newVisible) {
          // Check if we need to load:
          // 1. Just opened (newVisible !== oldVisible)
          // 2. URL changed while open (newUrl !== oldUrl)
          // 3. Initial load where old values might be undefined (immediate: true)
          const isJustOpened = newVisible !== oldVisible
          const urlChanged = newUrl !== oldUrl
          
          if (newUrl && (isJustOpened || urlChanged)) {
            if (isJustOpened) {
              // Reset state on open
              scale.value = 1.0
              imageScale.value = 1.0
            }
            // Use nextTick to ensure modal DOM is ready before loading PDF
            await nextTick()
            await loadPDF(newUrl)
          }
        } else {
          // Modal closed
          if (newVisible !== oldVisible) {
            await cleanupPDF()
          }
        }
      },
      { immediate: true },
    )

    // Navigation Actions
    const prevPdf = () => {
      if (currentIndex.value > 0) currentIndex.value--
    }

    const nextPdf = () => {
      if (currentIndex.value < props.pdfList.length - 1) currentIndex.value++
    }

    const previousPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
        renderPage(currentPage.value)
      }
    }

    const nextPage = () => {
      if (currentPage.value < numPages.value) {
        currentPage.value++
        renderPage(currentPage.value)
      }
    }

    const changeScale = (delta) => {
      const newScale = scale.value + delta
      if (newScale >= 0.5 && newScale <= 3.0) {
        scale.value = newScale
        renderPage(currentPage.value)
      }
    }

    const changeImageScale = (delta) => {
      const newScale = imageScale.value + delta
      if (newScale >= 0.5 && newScale <= 3.0) {
        imageScale.value = newScale
      }
    }

    const handleDownload = async () => {
      const url = currentPdfUrl.value
      if (!url) return

      try {
        let filename = 'document.pdf'
        if (props.pdfList.length > 0 && props.pdfList[currentIndex.value]) {
           filename = props.pdfList[currentIndex.value].name || `Document_${currentIndex.value + 1}.pdf`
        } else if (props.pdfPath) {
           const parts = props.pdfPath.split('/')
           filename = parts[parts.length - 1] || 'document.pdf'
        }
        if (!filename.toLowerCase().endsWith('.pdf')) filename += '.pdf'

        const response = await fetch(url)
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()

        setTimeout(() => {
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
        }, 100)
      } catch (err) {
        console.error('Download failed:', err)
        window.open(url, '_blank')
      }
    }

    const handleClose = () => {
      isVisible.value = false
      emit('close')
    }

    return {
      isVisible,
      currentIndex,
      currentPdfUrl,
      currentPdfName,
      isImageFile,
      // PDF
      canvasKey,
      pdfCanvas,
      currentPage,
      numPages,
      scale,
      loading,
      error,
      // Image
      imageScale,
      // Actions
      prevPdf,
      nextPdf,
      previousPage,
      nextPage,
      changeScale,
      changeImageScale,
      handleDownload,
      handleClose,
    }
  },
})
</script>

<style lang="scss" scoped>
/* Modal Container Overrides */
.pdf-preview-modal {
  :deep(.ant-modal-content) {
    border-radius: 8px;
    overflow: hidden;
  }

  :deep(.ant-modal-body) {
    display: flex;
    flex-direction: column;
    height: 85vh; /* Fixed height for consistent feel */
    max-height: 85vh;
  }
}

/* 1. Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;

  .modal-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #262626;

    @media (max-width: 576px) {
      font-size: 14px;
      max-width: 60%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .close-button {
    color: #8c8c8c;
    font-size: 18px;
    padding: 4px;
    &:hover { color: #262626; }
  }
}

/* 2. Main Content (Grey Background) */
.modal-content {
  flex: 1;
  position: relative;
  background: #525659; /* Standard PDF viewer background color */
  overflow: hidden; /* Prevent body scroll, use inner scroll */
  display: flex;
  flex-direction: column;

  /* Document Nav Buttons (Left/Right Arrows for switching files) */
  .nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(225, 15, 40, 0.8);
    border: none;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      background: #c40d23;
      transform: translateY(-50%) scale(1.1);
    }

    &:disabled {
      background: rgba(0,0,0,0.2);
      cursor: not-allowed;
    }

    &.nav-prev { left: 16px; }
    &.nav-next { right: 16px; }
  }
}

/* 3. PDF Container */
.pdf-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Loading & Error States */
.pdf-loading, .pdf-error, .no-pdf {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #fff; /* White text on dark background */
}

.pdf-error {
  color: #ffccc7; /* Light red for error */
  .error-icon { font-size: 48px; margin-bottom: 16px; }
}

.no-pdf {
  color: #d9d9d9;
  .no-pdf-icon { font-size: 64px; margin-bottom: 16px; }
}

/* 4. Controls & Canvas Wrapper */
.pdf-viewer-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.pdf-controls {
  background: #f0f0f0;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #d9d9d9;
  flex-shrink: 0;

  .page-info {
    font-size: 13px;
    font-weight: 500;
    color: #262626;
    min-width: 80px;
    text-align: center;
  }

  .zoom-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .zoom-level {
      font-size: 13px;
      font-weight: 500;
      min-width: 45px;
      text-align: center;
    }
  }

  @media (max-width: 576px) {
    flex-wrap: wrap;
    gap: 8px;
    .page-info { min-width: auto; }
  }
}

.pdf-canvas-wrapper {
  flex: 1;
  overflow: auto; /* Enable scrolling for the canvas */
  display: flex;
  justify-content: flex-start; /* Important: Start align to allow right-scroll */
  padding: 24px;

  /* Centers the canvas if smaller than viewport */
  canvas {
    display: block;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    margin: auto; /* Magic centering */
    background: white;
    flex-shrink: 0; /* Don't shrink canvas */
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
}

/* Image Viewer Styles */
.image-viewer-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.image-controls {
  background: #f0f0f0;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #d9d9d9;
  flex-shrink: 0;

  .zoom-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .zoom-level {
      font-size: 13px;
      font-weight: 500;
      min-width: 45px;
      text-align: center;
    }
  }
}

.image-canvas-wrapper {
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;

  .preview-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    background: white;
    transition: transform 0.2s ease;
    transform-origin: center center;
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
}

/* 5. Footer Info */
.pdf-info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;

  .pdf-name {
    font-size: 13px;
    color: #595959;
    font-weight: 500;
    max-width: 80%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pdf-counter {
    font-size: 13px;
    color: #8c8c8c;
  }
}
</style>

<style lang="scss">
/* Global styles */
.download-button {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  background: white;
  color: #e10f28;
  padding: 6px 16px;
  transition: all 0.3s;
  height: auto;
  line-height: 1.5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: #e10f28;
    color: white;
  }

  @media (max-width: 576px) {
    padding: 6px 10px;
    .btn-text { display: none; } /* Hide text on mobile */
  }
}
</style>
